/**
 * Debug utility for Duebit
 * 
 * This utility provides a consistent way to handle debugging across the application.
 * Debug logs are only shown when:
 * 1. NODE_ENV is 'development' AND
 * 2. Debug is explicitly enabled via localStorage or URL parameter
 */

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Check if debug is explicitly enabled
const isDebugEnabled = () => {
  // Check URL parameter
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    if (window.location.search.includes('debug=true')) {
      return true;
    }
  }
  
  // Check localStorage
  if (typeof localStorage !== 'undefined') {
    if (localStorage.getItem('duebit_debug') === 'true') {
      return true;
    }
  }
  
  return false;
};

// Debug is only enabled in development mode AND when explicitly enabled
export const DEBUG = isDevelopment && isDebugEnabled();

/**
 * Create a logger for a specific module
 * @param {string} module - The module name (e.g., 'OfflineApi', 'ServiceWorker')
 * @returns {Function} - A logging function for the module
 */
export const createLogger = (module) => {
  return (message, ...args) => {
    if (DEBUG) {
      console.log(`[${module}] ${message}`, ...args);
    }
  };
};

/**
 * Enable debugging
 * This will set the localStorage flag and reload the page
 */
export const enableDebugging = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('duebit_debug', 'true');
    
    // Notify service worker about debug state change
    updateServiceWorkerDebugState(true);
    
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }
};

/**
 * Disable debugging
 * This will remove the localStorage flag and reload the page
 */
export const disableDebugging = () => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('duebit_debug');
    
    // Notify service worker about debug state change
    updateServiceWorkerDebugState(false);
    
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }
};

/**
 * Update the service worker's debug state
 * @param {boolean} enabled - Whether debugging is enabled
 */
export const updateServiceWorkerDebugState = (enabled) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'DEBUG_STATE',
      enabled: enabled
    });
  }
};

// Set up service worker message handling
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'GET_DEBUG_STATE') {
      updateServiceWorkerDebugState(DEBUG);
    }
  });
}

// Add a global method to toggle debugging from the console
if (typeof window !== 'undefined' && isDevelopment) {
  window.toggleDebugging = () => {
    if (isDebugEnabled()) {
      disableDebugging();
    } else {
      enableDebugging();
    }
  };
  
  // Log instructions in development mode
  console.log(
    '%c Duebit Debugging %c' +
    '\n\nTo enable debugging: window.toggleDebugging()' +
    '\nOr: localStorage.setItem("duebit_debug", "true") and reload' +
    '\nOr: Add ?debug=true to the URL' +
    '\n\nTo disable: window.toggleDebugging()' +
    '\nOr: localStorage.removeItem("duebit_debug") and reload',
    'background: #007bff; color: white; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
    'font-weight: normal;'
  );
}

export default {
  DEBUG,
  createLogger,
  enableDebugging,
  disableDebugging,
  updateServiceWorkerDebugState
};
